
import ArtistRating from './ArtistRating.vue';
import ValueSlider from '../metrics/ValueSlider.vue';
import FlagLabel from '../metrics/FlagLabel.vue';
import { mapActions, mapState } from 'pinia';
import { useArtistsList } from '@/store/artistsList';
import { usePageStatus } from '@/store/pageStatus';
import MetricsSelector, { ScoreFilterWithColor } from '../MetricsSelector.vue';
import { useMetrics } from '@/store/metrics';
import WithColorMixin from '@/mixins/WithColorMixin.vue';
import { PropType } from 'vue';
import { FilterValues, NewScore, Score } from '@/types/score';
import { MetricType, Metric } from '@/types/metrics';
import { Artist } from '@/types/artist';
import { defineComponent } from 'vue';
import { debounce } from '@/utils';


type ArtistScore = {
  scoreId?: number
  values: FilterValues,
  metric: Metric
  rerender: boolean
}


const __default__ = defineComponent({
  name: 'ListElement',
  props: {
      artist: Object as PropType<Artist>,
      new: Boolean,
  },
  components: {
    "artist-rating": ArtistRating,
    "value-slider": ValueSlider,
    "flag-label": FlagLabel,
    "metric-selector": MetricsSelector,
  },
  mixins: [WithColorMixin],
  data () {
    return {
      editing: false,
      name: "",
      localArtist: {} as Artist,
      rating: 1,
      scores: {} as {[key: string]: ArtistScore},
      backupArtist: {} as Artist,
      metricsPanelActive: false,
      scoresToRemove: [] as number [],
      loading: false,
    }
  },
  computed: {
    ...mapState(useMetrics, ['metrics']),
    selectionMetrics(): ScoreFilterWithColor[] {
      const currentMetricsIds = Object.keys(this.scores)
      const newMetrics = this.metrics.filter(
        (metric: Metric) => {return !currentMetricsIds.includes(metric.id.toString())}
      ).map(
        (metric) => {
          return {
            filter: {
              metric,
              filterValues: {
                minValue: 0,
                maxValue: 5,
              },
            },
            selected: false,
            range: false,
          };
        }
      )
      const scores = Object.values(this.scores).map((score) => {
        return {
          filter: {
            metric: score.metric,
            filterValues: score.values,
          },
          selected: true,
          range: false,
        }
      })
      return [...scores, ...newMetrics]
    }
  },
  methods: {
    ...mapActions(usePageStatus, ["hideNewArtist", "setError"]),
    ...mapActions(useArtistsList, ["addArtist", "updateArtist"]),
    toggleEdit () {
      if (this.loading)
        return

      if (this.new) {
        this.hideNewArtist()
        return
      }

      if (this.editing) {
        this.localArtist = this.backupArtist
        this.setScores()
      } else {
        this.backupArtist = this.localArtist
      }

      this.editing = !this.editing
    },
    async edit () {
      if (this.loading)
        return
      Object.values(this.scores).forEach(
        score => {
          const value = score.metric.type === MetricType.flag ? score.values.minValue : score.values.maxValue
          if (score.scoreId === undefined)
            this.localArtist.newScores.push({
              metricId: score.metric.id,
              value,
            })
          else
            this.localArtist.scores.push({
              id: score.scoreId,
              metric: score.metric.name,
              type: score.metric.type,
              value,
            })
        }
      )
      this.localArtist.rating = this.rating
      this.loading = true
      try {
        if (this.new) {
          await this.addArtist(this.localArtist)
                    .then(() => { this.hideNewArtist() })
                    .catch(
                      (error) => {
                        console.log(error); this.setError("Unable to add artist: " + error.message)
                      }
                    )
        }
        else {
          await this.updateArtist(this.localArtist, this.scoresToRemove)
          this.setScores()
          this.editing = false
        }
      } finally {
        this.loading = false
      }
    },
    setScores () {
      this.scores = {}
      const metricsMap: {[key: string]: Metric}  = {}
      this.metrics.map((metric) => { metricsMap[metric.name] = metric; })
      this.localArtist.scores.forEach((score) => {
        const metric: Metric = metricsMap[score.metric]
        this.scores[metric.id] = {
          scoreId: score.id,
          metric,
          values: metric.type === MetricType.flag ? {minValue: score.value, maxValue: 0} : {minValue: 0, maxValue: score.value},
          rerender: true
        }

        debounce(() => {
          this.scores[metric.id].rerender = false
        }, 300)()
      })
      this.rating = this.localArtist.rating
      this.scoresToRemove = []
    },
    toggleMetricsPanel() {
      this.metricsPanelActive = !this.metricsPanelActive
    },  
    addOrEditScore(score: ScoreFilterWithColor) {
      const metricId = score.filter.metric.id
      if (metricId in this.scores) {
        this.scores[metricId].values = score.filter.filterValues
        this.scores[metricId].rerender = true
        
        debounce(() => {
          this.scores[metricId].rerender = false
        }, 300)()
      } else {
        debounce(() => {
          this.scores[metricId] = {
            values: score.filter.filterValues,
            metric: score.filter.metric,
            rerender: false
          }
        }, 300)()
      }        
    },
    removeScore(score: ArtistScore) {  
      if (score.scoreId !== undefined)
        this.scoresToRemove.push(score.scoreId)

      delete this.scores[score.metric.id]
    },
    removePanelScore(score: ScoreFilterWithColor) {
      const existingScore = this.scores[score.filter.metric.id]
      if (existingScore !== undefined && existingScore.scoreId !== undefined)
        this.scoresToRemove.push(existingScore.scoreId)

      delete this.scores[score.filter.metric.id]
    },
    isValue(score: ArtistScore) {
      return score.metric.type === MetricType.value
    },
    artistsLikeThis () {
      if (this.localArtist.id)
        this.$emit("artistsLikeThis")
    }
  },
  mounted () {
    const newArtist = {name: "New Artist", scores: [] as Score[], rating: 1, newScores: [] as NewScore[]}
    this.localArtist = this.new ? newArtist : this.artist ?? newArtist
    this.name = this.localArtist.name
    this.editing = this.new
    this.setScores()
  },
});


import { useCssVars as _useCssVars } from 'vue'
const __injectCSSVars__ = () => {
_useCssVars(_ctx => ({
  "4c4dc973": (_ctx.lightColor),
  "b9078172": (_ctx.darkColor)
}))}
const __setup__ = __default__.setup
__default__.setup = __setup__
  ? (props, ctx) => { __injectCSSVars__();return __setup__(props, ctx) }
  : __injectCSSVars__

export default __default__